'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { RoomEvent, type Room } from 'livekit-client';
import { isRoomEnded, subscribeCallEnded } from '@/lib/calls/callEndedStore';

export type ConnectionStatus = 'offline' | 'call' | 'ended' | 'restored' | null;
const OFFLINE_DEBOUNCE_MS = 1500;
const RECOVERY_NOTICE_MS = 3000;
const HEARTBEAT_INTERVAL_MS = 30_000;
const HEARTBEAT_TIMEOUT_MS = 8000;
const RETRY_INITIAL_MS = 5000;
const RETRY_MAX_MS = 60_000;

// One store, scheduler and in-flight request per browser tab, independent of
// route changes and the number of readers (banner, call header). No interval.
const listeners = new Set<() => void>();
const rooms = new Map<symbol, { name: string; state: string }>();
let stopListeningForEnded: (() => void) | undefined;
let status: ConnectionStatus = null;
let browserOnline = true;
let backendReachable = true;
let running = false;
let heartbeat: ReturnType<typeof setTimeout> | undefined;
let deadline: ReturnType<typeof setTimeout> | undefined;
let debounce: ReturnType<typeof setTimeout> | undefined;
let dismissal: ReturnType<typeof setTimeout> | undefined;
let request: AbortController | undefined;
let retryMs = RETRY_INITIAL_MS;

function publish(next: ConnectionStatus) {
  if (status === next) return;
  status = next;
  listeners.forEach(listener => listener());
}
type Problem = 'offline' | 'call' | 'ended';
const isVisible = (s: ConnectionStatus) => s === 'offline' || s === 'call' || s === 'ended';
function problem(): Problem | null {
  if (!browserOnline || !backendReachable) return 'offline';
  let callEnded = false;
  for (const { name, state } of rooms.values()) {
    if (state !== 'reconnecting' && state !== 'disconnected' && state !== 'signalReconnecting') continue;
    // A room whose call has ended (lib/calls/callEndedStore) is not an outage: LiveKit keeps retrying a deleted room.
    if (isRoomEnded(name)) callEnded = true; else return 'call';
  }
  return callEnded ? 'ended' : null;
}
function reconcile() {
  if (!running) return;
  const next = problem();
  if (next === 'ended') {
    // The server has said so: no debounce, there is nothing to wait out.
    clearTimeout(debounce); debounce = undefined;
    clearTimeout(dismissal); dismissal = undefined;
    publish('ended');
    return;
  }
  if (next) {
    clearTimeout(dismissal); dismissal = undefined;
    // Once an outage is visible, update its reason in the SAME banner. Repeated
    // events must not restart the debounce and postpone the notice forever.
    if (isVisible(status)) { publish(next); return; }
    if (status === 'restored') publish(null);
    if (debounce === undefined) {
      debounce = setTimeout(() => {
        debounce = undefined;
        const current = problem();
        if (current) publish(current);
      }, OFFLINE_DEBOUNCE_MS);
    }
    return;
  }
  clearTimeout(debounce); debounce = undefined;
  if (isVisible(status)) {
    // Leaving a call removes its signal; it is not a successful reconnection. Neither is the end of a call.
    if (status === 'ended' || (status === 'call' && rooms.size === 0)) { publish(null); return; }
    publish('restored');
    dismissal = setTimeout(() => { dismissal = undefined; publish(null); }, RECOVERY_NOTICE_MS);
  }
}
function pauseHeartbeat() {
  clearTimeout(heartbeat); heartbeat = undefined;
  clearTimeout(deadline); deadline = undefined;
  // Clear the identity FIRST: a late fetch result after pause/unmount cannot
  // publish recovery or create a second scheduler.
  const previous = request; request = undefined; previous?.abort();
}
function canProbe() { return running && browserOnline && document.visibilityState !== 'hidden'; }
async function probe() {
  if (!canProbe() || request) return;
  clearTimeout(heartbeat); heartbeat = undefined;
  const controller = new AbortController(); request = controller;
  deadline = setTimeout(() => controller.abort(), HEARTBEAT_TIMEOUT_MS);
  let reachable = false;
  try {
    const response = await fetch('/api/health', {
      method: 'GET', cache: 'no-store', credentials: 'same-origin',
      redirect: 'error', signal: controller.signal,
    });
    // This existing endpoint also checks the backend. HTTP 200 by itself is
    // insufficient: captive portals return HTML, and backend outages are 200
    // with checks.backend.status=degraded. Memory degradation is unrelated.
    if (response.ok && !response.redirected) {
      const body = await response.json();
      reachable = body?.service === 'ui-frontend' && body?.checks?.backend?.status === 'ok';
    }
  } catch {
    // Network/timeout/JSON failures are the expected input to the status UI.
  }
  if (request !== controller || !running) return;
  clearTimeout(deadline); deadline = undefined; request = undefined;
  backendReachable = reachable;
  reconcile();
  if (!canProbe()) return;
  const delay = reachable ? HEARTBEAT_INTERVAL_MS : retryMs;
  retryMs = reachable ? RETRY_INITIAL_MS : Math.min(retryMs * 2, RETRY_MAX_MS);
  heartbeat = setTimeout(() => { heartbeat = undefined; void probe(); }, delay);
}
function onNetwork() {
  const wasOnline = browserOnline;
  browserOnline = navigator.onLine;
  if (!browserOnline) {
    backendReachable = false;
    pauseHeartbeat();
  } else {
    // The online event only means a local interface is up. Confirm the route
    // before saying Back online. Repeated online events share the same probe.
    if (!wasOnline) { retryMs = RETRY_INITIAL_MS; void probe(); }
  }
  reconcile();
}
function onVisibility() {
  if (document.visibilityState === 'hidden') pauseHeartbeat();
  else {
    browserOnline = navigator.onLine;
    if (!browserOnline) backendReachable = false;
    reconcile(); void probe();
  }
}
function start() {
  running = true;
  browserOnline = navigator.onLine;
  if (!browserOnline) backendReachable = false;
  window.addEventListener('online', onNetwork);
  window.addEventListener('offline', onNetwork);
  document.addEventListener('visibilitychange', onVisibility);
  stopListeningForEnded = subscribeCallEnded(reconcile);
  reconcile();
  void probe();
}
function stop() {
  running = false;
  window.removeEventListener('online', onNetwork);
  window.removeEventListener('offline', onNetwork);
  document.removeEventListener('visibilitychange', onVisibility);
  stopListeningForEnded?.(); stopListeningForEnded = undefined;
  pauseHeartbeat();
  clearTimeout(debounce); debounce = undefined;
  clearTimeout(dismissal); dismissal = undefined;
  status = null; backendReachable = true; retryMs = RETRY_INITIAL_MS;
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) start();
  return () => { listeners.delete(listener); if (listeners.size === 0) stop(); };
}
const snapshot = () => status;
const serverSnapshot = () => null;
export function useConnectionStatus(): ConnectionStatus {
  return useSyncExternalStore(subscribe, snapshot, serverSnapshot);
}

/** Observes the existing room; never creates or connects a LiveKit room. */
export function useRoomConnectionStatus(room: Room, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const key = Symbol('room');
    const update = () => { rooms.set(key, { name: room.name, state: room.state }); reconcile(); };
    room.on(RoomEvent.ConnectionStateChanged, update);
    update();
    return () => {
      room.off(RoomEvent.ConnectionStateChanged, update);
      rooms.delete(key);
      reconcile();
    };
  }, [room, enabled]);
}
